import { GridOption } from '@zoryn/components/grid'
import type { GridColumnProps } from '@zoryn/components/grid'
import type { RuleRecord, RulesType } from './rules'

function formatRulesJson(value: unknown) {
  if (value == null) return 'null'
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

export interface RulesListGridHandlers {
  onCurrentRowChange?: (row: RuleRecord | null) => void
  onEnabledChange?: (row: RuleRecord, enabled: boolean) => void
}

function asRuleRecord(row: Record<string, unknown>): RuleRecord {
  return row as unknown as RuleRecord
}

export function buildRulesListColumns(
  t: (key: string) => string,
  type: RulesType = 'system'
): GridColumnProps[] {
  const columns: GridColumnProps[] = [
    { type: 'checkbox', width: 44, fixed: 'left' },
    {
      field: 'system',
      title: t('z.rules.colSystem'),
      width: 88,
      editable: false,
    },
    {
      field: 'ruleName',
      title: t('z.rules.colRuleName'),
      width: 140,
      editable: false,
    },
    {
      field: 'ruleDescription',
      title: t('z.rules.colRuleDescription'),
      minWidth: 180,
      editable: false,
    },
    {
      field: 'module',
      title: t('z.rules.colModule'),
      width: 100,
      editable: false,
    },
  ]

  if (type !== 'user') {
    columns.push(
      {
        field: 'ruleScript',
        title: t('z.rules.colRuleScript'),
        minWidth: 180,
        editable: false,
      },
      {
        type: 'switch',
        field: 'enabled',
        title: t('z.rules.colEnabled'),
        width: 100,
        minWidth: 100,
        editable: true,
      },
      {
        field: 'rulesJson',
        title: t('z.rules.colRulesJson'),
        minWidth: 180,
        editable: false,
        formatter: ({ cellValue }) => formatRulesJson(cellValue),
      }
    )
  } else {
    columns.push({
      type: 'switch',
      field: 'enabled',
      title: t('z.rules.colEnabled'),
      width: 100,
      minWidth: 100,
      editable: true,
    })
  }

  return columns
}

export function createRulesListGridOptions(
  t: (key: string) => string,
  type: RulesType = 'system',
  handlers: RulesListGridHandlers = {}
) {
  return new GridOption({
    module: '/system/rules',
    autoQuery: false,
    sideBar: false,
    editable: true,
    dblClickEdit: false,
    onCurrentRowChange: (row) => {
      handlers.onCurrentRowChange?.(row ? asRuleRecord(row) : null)
    },
    onCellValueChange: ({ row, field, value }) => {
      if (field !== 'enabled') {
        return
      }
      handlers.onEnabledChange?.(asRuleRecord(row), Boolean(value))
    },
    columns: buildRulesListColumns(t, type),
  })
}
